/**
 * ChatTab — split-panel conversation-based messaging.
 * Phase 26b: iMessage-style conversations per job.
 * Left: conversation list. Right: active message thread.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  fetchConversations, createConversation,
  fetchMessages, sendMessage,
} from '../../../api/messages';
import { useAuth } from '../../../context/AuthContext';
import { Button, LoadingSpinner } from '../../common';
import client from '../../../api/client';

// ── Helpers ──────────────────────────────────────────────────────────

function fmtTime(d) {
  if (!d) return '';
  try { return format(parseISO(d), 'h:mm a'); } catch { return d; }
}
function fmtDate(d) {
  if (!d) return '';
  try {
    const date = parseISO(d);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) return 'Today';
    if (format(date, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) return 'Yesterday';
    return format(date, 'EEE, MMM d');
  } catch { return d; }
}
function isSameDay(d1, d2) {
  if (!d1 || !d2) return false;
  try { return format(parseISO(d1), 'yyyy-MM-dd') === format(parseISO(d2), 'yyyy-MM-dd'); } catch { return false; }
}
function timeAgo(d) {
  if (!d) return '';
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const TYPE_STYLES = {
  assignment: { bg: 'bg-purple-50', border: 'border-purple-200', label: 'text-purple-600', icon: '📋' },
  instruction: { bg: 'bg-amber-50', border: 'border-amber-200', label: 'text-amber-600', icon: '📝' },
  system: { bg: 'bg-gray-50', border: 'border-gray-200', label: 'text-gray-500', icon: '⚙️' },
};

// ── Main Component ───────────────────────────────────────────────────

export default function ChatTab({ job }) {
  const { user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [body, setBody] = useState('');
  const [msgType, setMsgType] = useState('chat');
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newConvoMsg, setNewConvoMsg] = useState('');
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Load conversations
  const loadConvos = useCallback(async () => {
    try {
      const data = await fetchConversations(job.id);
      setConvos(data);
      // Auto-select first if none active
      if (!activeConvoId && data.length > 0) setActiveConvoId(data[0].id);
    } catch {} finally { setLoadingConvos(false); }
  }, [job.id]);

  // Load messages for active conversation
  const loadMessages = useCallback(async () => {
    if (!activeConvoId) return;
    try {
      const data = await fetchMessages(job.id, activeConvoId);
      setMessages(data);
    } catch {}
  }, [job.id, activeConvoId]);

  // Load users for new conversation picker
  useEffect(() => {
    client.get('/auth/users/').then(({ data }) => {
      setAllUsers(data.data || data.results || data || []);
    }).catch(() => {});
  }, []);

  // Initial load + polling
  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => {
    if (!activeConvoId) { setMessages([]); return; }
    setLoadingMsgs(true);
    loadMessages().finally(() => setLoadingMsgs(false));
  }, [activeConvoId]);

  // Poll
  useEffect(() => {
    pollRef.current = setInterval(() => { loadConvos(); if (activeConvoId) loadMessages(); }, 15000);
    return () => clearInterval(pollRef.current);
  }, [loadConvos, loadMessages, activeConvoId]);

  // Auto-scroll
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  // Send message
  async function handleSend() {
    if (!body.trim() || !activeConvoId) return;
    setSending(true);
    try {
      const payload = { body: body.trim(), message_type: msgType };
      if (replyTo) payload.reply_to = replyTo.id;
      await sendMessage(job.id, activeConvoId, payload);
      setBody(''); setReplyTo(null); setMsgType('chat');
      loadMessages(); loadConvos();
    } catch (err) { alert(err.response?.data?.error || 'Send failed'); }
    finally { setSending(false); }
  }

  // Create new conversation
  async function handleCreateConvo() {
    if (selectedUsers.length === 0) return;
    try {
      const result = await createConversation(job.id, {
        participant_ids: selectedUsers.map(u => u.id),
        initial_message: newConvoMsg.trim() || undefined,
      });
      setShowNewConvo(false); setSelectedUsers([]); setNewConvoMsg('');
      await loadConvos();
      setActiveConvoId(result.id);
    } catch (err) { alert(err.response?.data?.error || 'Failed to create conversation'); }
  }

  function toggleUser(u) {
    setSelectedUsers(prev => prev.find(s => s.id === u.id) ? prev.filter(s => s.id !== u.id) : [...prev, u]);
  }

  const isMe = (sid) => sid === user?.id;
  const activeConvo = convos.find(c => c.id === activeConvoId);

  return (
    <div className="bg-white rounded-lg border border-gray-200 flex" style={{ height: 'calc(100vh - 320px)', minHeight: '450px' }}>
      {/* ── Left: Conversation List ── */}
      <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col">
        <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Conversations</span>
          <button onClick={() => setShowNewConvo(true)} className="text-blue-600 hover:text-blue-700 text-xs font-medium" title="New conversation">
            + New
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingConvos ? (
            <div className="flex justify-center py-8"><LoadingSpinner /></div>
          ) : convos.length === 0 ? (
            <div className="py-8 px-3 text-center">
              <p className="text-xs text-gray-400 mb-2">No conversations yet.</p>
              <button onClick={() => setShowNewConvo(true)} className="text-xs text-blue-600 font-medium">Start a conversation</button>
            </div>
          ) : (
            convos.map(c => {
              const active = c.id === activeConvoId;
              const lm = c.last_message;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveConvoId(c.id)}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`text-sm font-medium truncate ${active ? 'text-blue-700' : 'text-gray-900'}`}>{c.display_title}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      {lm && <span className="text-[10px] text-gray-400">{timeAgo(lm.created_at)}</span>}
                      {c.unread_count > 0 && (
                        <span className="min-w-[18px] h-[18px] bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  {lm && (
                    <p className="text-xs text-gray-500 truncate">
                      <span className="font-medium">{lm.sender_initials}</span> {lm.body}
                    </p>
                  )}
                  <div className="flex gap-1 mt-0.5">
                    {c.participants_display?.map(p => (
                      <span key={p.id} className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[8px] font-bold" title={p.name}>
                        {p.initials}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── Right: Message Thread ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {!activeConvoId ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <svg className="w-10 h-10 mx-auto mb-2 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">Select a conversation or start a new one.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">{activeConvo?.display_title}</div>
                <div className="text-[10px] text-gray-400">
                  {activeConvo?.participants_display?.map(p => p.name).join(', ')}
                </div>
              </div>
              <span className="text-xs text-gray-400">{messages.length} messages</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5">
              {loadingMsgs ? (
                <div className="flex justify-center py-8"><LoadingSpinner /></div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">No messages yet.</div>
              ) : (
                messages.map((msg, i) => {
                  const prev = i > 0 ? messages[i - 1] : null;
                  const showDate = !prev || !isSameDay(prev.created_at, msg.created_at);
                  const mine = isMe(msg.sender);
                  const isSpecial = msg.message_type !== 'chat';
                  const sameSender = prev && prev.sender === msg.sender && isSameDay(prev.created_at, msg.created_at);
                  const style = TYPE_STYLES[msg.message_type];

                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div className="flex items-center gap-3 my-3">
                          <div className="flex-1 border-t border-gray-200" />
                          <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">{fmtDate(msg.created_at)}</span>
                          <div className="flex-1 border-t border-gray-200" />
                        </div>
                      )}

                      {isSpecial ? (
                        /* Assignment / Instruction card */
                        <div className={`my-2 rounded-lg border px-3 py-2 ${style.bg} ${style.border}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${msg.message_type === 'assignment' ? 'bg-purple-500' : 'bg-amber-500'}`} title={msg.sender_name}>
                              {msg.sender_initials}
                            </div>
                            <span className="text-sm">{style.icon}</span>
                            <span className={`text-[10px] font-semibold uppercase tracking-wide ${style.label}`}>{msg.message_type}</span>
                            <span className="text-[10px] text-gray-400">from {msg.sender_name}</span>
                            <span className="text-[10px] text-gray-300 ml-auto">{fmtTime(msg.created_at)}</span>
                          </div>
                          {msg.reply_to_preview && (
                            <div className="mb-1.5 pl-2 border-l-2 border-gray-300 text-xs text-gray-500 italic">
                              <span className="font-medium">{msg.reply_to_preview.sender_name}:</span> {msg.reply_to_preview.body}
                            </div>
                          )}
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.body}</p>
                          {msg.mentioned_user_names?.length > 0 && (
                            <div className="flex items-center gap-1 mt-1.5">
                              <span className="text-[10px] text-gray-400">Assigned to:</span>
                              {msg.mentioned_user_names.map(u => (
                                <span key={u.id} className="px-1.5 py-0.5 rounded-full bg-white text-[10px] font-medium text-gray-700 border border-gray-200">{u.name}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Regular chat bubble with initials */
                        <div className={`flex ${mine ? 'justify-end' : 'justify-start'} ${sameSender ? 'mt-0.5' : 'mt-3'}`}>
                          {!mine && (
                            <div className="flex-shrink-0 mr-2 mt-auto">
                              {!sameSender ? (
                                <div className="w-7 h-7 rounded-full bg-gray-400 text-white flex items-center justify-center text-[10px] font-bold" title={msg.sender_name}>{msg.sender_initials}</div>
                              ) : <div className="w-7" />}
                            </div>
                          )}
                          <div className={`max-w-[70%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                            {!sameSender && (
                              <span className={`text-[11px] font-medium mb-0.5 ${mine ? 'text-blue-500 mr-1' : 'text-gray-500 ml-1'}`}>{msg.sender_name}</span>
                            )}
                            {/* Quoted reply */}
                            {msg.reply_to_preview && (
                              <div className={`text-[10px] mb-0.5 px-2 py-1 rounded border ${mine ? 'bg-blue-500/20 border-blue-400/30 text-blue-100 mr-1' : 'bg-gray-100 border-gray-200 text-gray-500 ml-1'}`}>
                                <span className="font-semibold">{msg.reply_to_preview.sender_initials}</span> {msg.reply_to_preview.body.slice(0, 60)}{msg.reply_to_preview.body.length > 60 ? '...' : ''}
                              </div>
                            )}
                            {/* Bubble — click to reply */}
                            <button
                              onClick={() => setReplyTo(msg)}
                              className={`px-3 py-1.5 rounded-2xl text-sm text-left ${mine ? 'bg-blue-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'} hover:opacity-90 transition-opacity`}
                              title="Click to reply"
                            >
                              <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                            </button>
                            <div className={`text-[10px] text-gray-400 mt-0.5 ${mine ? 'mr-1' : 'ml-1'}`}>{fmtTime(msg.created_at)}</div>
                          </div>
                          {mine && (
                            <div className="flex-shrink-0 ml-2 mt-auto">
                              {!sameSender ? (
                                <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold" title={msg.sender_name}>{msg.sender_initials}</div>
                              ) : <div className="w-7" />}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply bar */}
            {replyTo && (
              <div className="px-4 py-1.5 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
                <div className="w-1 h-8 bg-blue-400 rounded-full flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-medium text-blue-600">Replying to {replyTo.sender_name}</div>
                  <div className="text-xs text-gray-500 truncate">{replyTo.body}</div>
                </div>
                <button onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-gray-600 text-sm">✕</button>
              </div>
            )}

            {/* Input */}
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex items-end gap-2">
                {/* Type selector */}
                <select
                  value={msgType}
                  onChange={e => setMsgType(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-2 bg-gray-50 text-gray-600"
                >
                  <option value="chat">💬 Chat</option>
                  <option value="assignment">📋 Assignment</option>
                  <option value="instruction">📝 Instruction</option>
                </select>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Type a message..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={1}
                  style={{ minHeight: '38px', maxHeight: '100px' }}
                  onInput={e => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
                />
                <button
                  onClick={handleSend}
                  disabled={sending || !body.trim()}
                  className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
              <div className="text-[10px] text-gray-400 mt-1">Enter to send · Shift+Enter for new line · Click a message to reply</div>
            </div>
          </>
        )}
      </div>

      {/* ── New Conversation Modal ── */}
      {showNewConvo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowNewConvo(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">New Conversation</h3>
              <p className="text-xs text-gray-500 mt-0.5">Select users to include in this conversation.</p>
            </div>
            <div className="px-5 py-4">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Participants</label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1 mb-3">
                {allUsers.filter(u => u.id !== user?.id).map(u => {
                  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                  const sel = selectedUsers.find(s => s.id === u.id);
                  return (
                    <label key={u.id} className={`flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-2 py-1 ${sel ? 'bg-blue-50' : ''}`}>
                      <input type="checkbox" checked={!!sel} onChange={() => toggleUser(u)} className="rounded border-gray-300" />
                      <span className="font-medium">{name}</span>
                    </label>
                  );
                })}
                {allUsers.filter(u => u.id !== user?.id).length === 0 && (
                  <p className="text-xs text-gray-400 py-2 text-center">No other users found. Create users in Manage → Users.</p>
                )}
              </div>
              {selectedUsers.length > 0 && (
                <div className="flex gap-1 mb-3 flex-wrap">
                  {selectedUsers.map(u => (
                    <span key={u.id} className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                      {u.first_name || u.email}
                    </span>
                  ))}
                </div>
              )}
              <label className="text-xs font-medium text-gray-600 mb-1 block">First Message (optional)</label>
              <textarea
                value={newConvoMsg}
                onChange={e => setNewConvoMsg(e.target.value)}
                placeholder="Type your first message..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                rows={3}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowNewConvo(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreateConvo} disabled={selectedUsers.length === 0}>Create</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
